import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { trpc } from "@/lib/trpc";
import {
  Search,
  Scale,
  AlertTriangle,
  Clock,
  CheckCircle2,
  XCircle,
  Plus,
  Eye,
  ArrowUpDown,
  RefreshCw,
  WifiOff,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { canWrite } from "@/lib/rbac";
import { ListPageSkeleton } from "@/components/PageSkeleton";
import { EmptyState } from "@/components/shared/EmptyState";
import { formatDate } from "@/lib/demo-data";
import { toast } from "sonner";

const STATUS_CONFIG: Record<
  string,
  { label: string; variant: "default" | "secondary" | "destructive" | "outline"; icon: typeof Clock }
> = {
  REPORTED: { label: "Signale", variant: "outline", icon: AlertTriangle },
  OPEN: { label: "Ouvert", variant: "default", icon: Scale },
  IN_MEDIATION: { label: "En mediation", variant: "secondary", icon: Clock },
  RESOLVED: { label: "Resolu", variant: "default", icon: CheckCircle2 },
  CLOSED: { label: "Clos", variant: "outline", icon: XCircle },
  ESCALATED: { label: "Escalade", variant: "destructive", icon: AlertTriangle },
};

const REASON_LABELS: Record<string, string> = {
  POOR_QUALITY: "Mauvaise qualite des travaux",
  DELAY: "Retard important",
  NO_SHOW: "Artisan absent",
  PRICE_DISPUTE: "Litige sur le prix",
  DAMAGE: "Degats causes",
  NON_PAYMENT: "Non-paiement",
  INCOMPLETE_WORK: "Travaux incomplets",
  OTHER: "Autre",
};

const RESOLUTION_LABELS: Record<string, string> = {
  CREDIT_REFUND: "Remboursement credits",
  WARNING: "Avertissement",
  SUSPENSION: "Suspension",
  NO_ACTION: "Sans suite",
  MUTUAL_AGREEMENT: "Accord a l'amiable",
};

type StatusFilter = "all" | "REPORTED" | "OPEN" | "IN_MEDIATION" | "RESOLVED" | "ESCALATED" | "CLOSED";

export default function Disputes() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isWriter = canWrite(user?.staffRole || null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [reasonFilter, setReasonFilter] = useState<string>("");

  const { data, isLoading, isError, refetch } = trpc.admin.disputes.list.useQuery(
    {
      page,
      limit: 20,
      search: search || undefined,
      status: statusFilter !== "all" ? statusFilter : undefined,
      reason: reasonFilter || undefined,
    } as any,
    { retry: false }
  );

  const disputes = (data as any)?.items ?? [];
  const totalPages = (data as any)?.totalPages ?? 1;
  const stats = (data as any)?.stats;

  if (isLoading) {
    return (
      <AdminLayout>
        <ListPageSkeleton />
      </AdminLayout>
    );
  }

  if (isError) {
    return (
      <AdminLayout>
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Litiges & Mediation</h1>
            <p className="text-muted-foreground">
              Gestion des litiges entre particuliers et professionnels
            </p>
          </div>
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <WifiOff className="h-10 w-10 text-muted-foreground mb-4" />
              <p className="text-sm text-muted-foreground mb-4">
                Les endpoints backend pour les litiges ne sont pas encore disponibles.
              </p>
              <Button variant="outline" onClick={() => refetch()}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Reessayer
              </Button>
            </CardContent>
          </Card>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Litiges & Mediation</h1>
            <p className="text-muted-foreground">
              Gestion des litiges entre particuliers et professionnels
            </p>
          </div>
          {isWriter && (
            <Button onClick={() => navigate("/disputes/new")}>
              <Plus className="h-4 w-4 mr-2" />
              Nouveau litige
            </Button>
          )}
        </div>

        {/* Stats cards */}
        {stats && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardContent className="p-4">
                <div className="text-sm text-muted-foreground">Signales</div>
                <div className="text-2xl font-bold">{stats.reported ?? 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-sm text-muted-foreground">En mediation</div>
                <div className="text-2xl font-bold">{stats.inMediation ?? 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-sm text-muted-foreground">Resolus</div>
                <div className="text-2xl font-bold">{stats.resolved ?? 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-sm text-muted-foreground">Escalades</div>
                <div className="text-2xl font-bold text-destructive">{stats.escalated ?? 0}</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher par entreprise, particulier..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="pl-9"
            />
          </div>
          <Select
            value={statusFilter}
            onValueChange={(v) => {
              setStatusFilter(v as StatusFilter);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              <SelectItem value="REPORTED">Signales</SelectItem>
              <SelectItem value="OPEN">Ouverts</SelectItem>
              <SelectItem value="IN_MEDIATION">En mediation</SelectItem>
              <SelectItem value="ESCALATED">Escalades</SelectItem>
              <SelectItem value="RESOLVED">Resolus</SelectItem>
              <SelectItem value="CLOSED">Clos</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={reasonFilter}
            onValueChange={(v) => {
              setReasonFilter(v === "all" ? "" : v);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Motif" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les motifs</SelectItem>
              {Object.entries(REASON_LABELS).map(([key, label]) => (
                <SelectItem key={key} value={key}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>

        {/* Table */}
        {disputes.length === 0 ? (
          <EmptyState
            icon={<Scale className="h-8 w-8" />}
            title="Aucun litige"
            description={
              statusFilter === "all"
                ? "Aucun litige enregistre"
                : "Aucun litige ne correspond aux filtres"
            }
          />
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-24">Ref</TableHead>
                    <TableHead>Particulier</TableHead>
                    <TableHead>Pro</TableHead>
                    <TableHead>Motif</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Priorite</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="w-16" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {disputes.map((dispute: any) => {
                    const status = STATUS_CONFIG[dispute.status] ?? STATUS_CONFIG.REPORTED;
                    const StatusIcon = status.icon;
                    return (
                      <TableRow
                        key={dispute.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => navigate(`/disputes/${dispute.id}`)}
                      >
                        <TableCell className="font-mono text-xs">
                          {dispute.reference ?? dispute.id?.slice(0, 8)}
                        </TableCell>
                        <TableCell className="font-medium">
                          {dispute.particulierName ?? "Particulier"}
                        </TableCell>
                        <TableCell>{dispute.Company?.name ?? "Entreprise"}</TableCell>
                        <TableCell className="text-sm">
                          {REASON_LABELS[dispute.reason] ?? dispute.reason}
                        </TableCell>
                        <TableCell>
                          <Badge variant={status.variant} className="gap-1">
                            <StatusIcon className="h-3 w-3" />
                            {status.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              dispute.priority === "URGENT"
                                ? "destructive"
                                : dispute.priority === "HIGH"
                                ? "default"
                                : "outline"
                            }
                          >
                            {dispute.priority === "URGENT"
                              ? "Urgent"
                              : dispute.priority === "HIGH"
                              ? "Haute"
                              : dispute.priority === "MEDIUM"
                              ? "Moyenne"
                              : "Basse"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {dispute.createdAt ? formatDate(dispute.createdAt) : "-"}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Precedent
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Suivant
            </Button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
